import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaEdit, FaPlus } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';

const initialForm = {
  id: null,
  label_en: '',
  label_ar: '',
  code: '',
  sort_order: '',
  is_active: true,
  image: null,
};

const BoreholeLegends = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [legends, setLegends] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const toImageUrl = (legend) => {
    if (!legend?.id) return null;
    // مسار التخزين المباشر (public/storage) مش متظبط على السيرفر، فبنستخدم الراوت المخصص لعرض صورة الرمز
    if (legend.image_path && /^https?:\/\//i.test(legend.image_path)) return legend.image_path;
    return `${BASE_URL}/legends/${legend.id}/image`;
  };

  const loadLegends = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${BASE_URL}/legends`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data?.data ?? res.data ?? [];
      setLegends(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(t('boreholes.toasts.fetchLegendsError', { defaultValue: 'تعذر تحميل مكتبة الرموز' }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLegends();
  }, []);

  const handleSubmit = async () => {
    if (!form.label_en) {
      toast.warn(t('boreholes.toasts.legendRequired', { defaultValue: 'الاسم الإنجليزي والصورة مطلوبان' }));
      return;
    }
    if (!form.id && !form.image) {
      toast.warn(t('boreholes.toasts.legendRequired', { defaultValue: 'الاسم الإنجليزي والصورة مطلوبان' }));
      return;
    }
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('label_en', form.label_en || '');
      fd.append('label_ar', form.label_ar || '');
      fd.append('code', form.code || '');
      fd.append('sort_order', form.sort_order !== '' ? String(form.sort_order) : '0');
      fd.append('is_active', form.is_active ? '1' : '0');
      if (form.image) fd.append('image', form.image);

      if (form.id) {
        await axios.patch(`${BASE_URL}/legends/${form.id}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${BASE_URL}/legends`, fd, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t('common.success', { defaultValue: 'تمت العملية بنجاح' }));
      setShowForm(false);
      setForm(initialForm);
      loadLegends();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.error', { defaultValue: 'حدث خطأ غير متوقع' }));
    }
  };

  const toggleLegend = async (legend) => {
    setForm({
      id: legend.id,
      label_en: legend.label_en || '',
      label_ar: legend.label_ar || '',
      code: legend.code || '',
      sort_order: legend.sort_order ?? '',
      is_active: !legend.is_active,
      image: null,
    });
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('label_en', legend.label_en || '');
      fd.append('label_ar', legend.label_ar || '');
      fd.append('code', legend.code || '');
      fd.append('sort_order', legend.sort_order !== '' ? String(legend.sort_order) : '0');
      fd.append('is_active', legend.is_active ? '0' : '1');
      await axios.patch(`${BASE_URL}/legends/${legend.id}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      loadLegends();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.error', { defaultValue: 'حدث خطأ غير متوقع' }));
    } finally {
      setForm(initialForm);
    }
  };

  return (
    <div style={{ padding: 24, backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={() => navigate('/boreholes')} style={btnSecondary}>
          {isRTL ? <FaArrowRight /> : <FaArrowLeft />} {t('boreholes.back', { defaultValue: 'رجوع' })}
        </button>
        <h2 style={{ margin: 0 }}>{t('boreholes.legendLibrary')}</h2>
        <button
          onClick={() => { setForm(initialForm); setShowForm(true); }}
          style={btnPrimary}
        >
          <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>{t('loading', { defaultValue: 'جاري التحميل...' })}</div>
      ) : (
        <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>{t('boreholes.layers.legend', { defaultValue: 'Legend' })}</th>
                  <th style={thStyle}>{t('boreholes.legends.code', { defaultValue: 'الكود' })}</th>
                  <th style={thStyle}>{t('boreholes.legends.labelAr', { defaultValue: 'الاسم عربي' })}</th>
                  <th style={thStyle}>{t('boreholes.legends.labelEn', { defaultValue: 'الاسم إنجليزي' })}</th>
                  <th style={thStyle}>{t('boreholes.legends.sortOrder', { defaultValue: 'الترتيب' })}</th>
                  <th style={thStyle}>{t('common.active', { defaultValue: 'نشط' })}</th>
                  <th style={thStyle}>{t('table.actions', { defaultValue: 'الإجراءات' })}</th>
                </tr>
              </thead>
              <tbody>
                {legends.map((legend, index) => (
                  <tr key={legend.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {toImageUrl(legend) ? (
                        <img src={toImageUrl(legend)} alt={legend.label_en || `legend-${legend.id}`} style={{ width: 60, height: 60, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
                      ) : '-'}
                    </td>
                    <td style={tdStyle}>{legend.code || '-'}</td>
                    <td style={tdStyle}>{legend.label_ar || '-'}</td>
                    <td style={tdStyle}>{legend.label_en || '-'}</td>
                    <td style={tdStyle}>{legend.sort_order ?? '-'}</td>
                    <td style={tdStyle}>{legend.is_active ? t('common.yes', { defaultValue: 'نعم' }) : t('common.no', { defaultValue: 'لا' })}</td>
                    <td style={{ ...tdStyle, minWidth: 180 }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          type="button"
                          style={btnSecondary}
                          onClick={() => {
                            setForm({
                              id: legend.id,
                              label_en: legend.label_en || '',
                              label_ar: legend.label_ar || '',
                              code: legend.code || '',
                              sort_order: legend.sort_order ?? '',
                              is_active: !!legend.is_active,
                              image: null,
                            });
                            setShowForm(true);
                          }}
                        >
                          <FaEdit /> {t('btns.edit', { defaultValue: 'تعديل' })}
                        </button>
                        <button type="button" style={btnDanger} onClick={() => toggleLegend(legend)}>
                          {legend.is_active ? t('common.disable', { defaultValue: 'تعطيل' }) : t('common.enable', { defaultValue: 'تفعيل' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {legends.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#c7d2e3' }}>
                      {t('noData', { defaultValue: 'لا توجد بيانات' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #333' }}>
                <h5 className="modal-title">{form.id ? t('btns.edit', { defaultValue: 'تعديل' }) : t('common.add', { defaultValue: 'إضافة' })}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowForm(false)} />
              </div>
              <div className="modal-body">
                <div style={{ display: 'grid', gap: 10 }}>
                  <input value={form.label_en} onChange={(e) => setForm((p) => ({ ...p, label_en: e.target.value }))} placeholder={t('boreholes.legends.labelEn', { defaultValue: 'الاسم إنجليزي' })} style={inputStyle} />
                  <input value={form.label_ar} onChange={(e) => setForm((p) => ({ ...p, label_ar: e.target.value }))} placeholder={t('boreholes.legends.labelAr', { defaultValue: 'الاسم عربي' })} style={inputStyle} />
                  <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} placeholder={t('boreholes.legends.code', { defaultValue: 'الكود' })} style={inputStyle} />
                  <input type="number" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))} placeholder={t('boreholes.legends.sortOrder', { defaultValue: 'الترتيب' })} style={inputStyle} />
                  <input type="file" accept="image/*" onChange={(e) => setForm((p) => ({ ...p, image: e.target.files?.[0] || null }))} style={inputStyle} />
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                    {t('common.active', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #333' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  {t('delete.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button type="button" className="btn btn-danger" style={{ backgroundColor: '#AC2000', border: 'none' }} onClick={handleSubmit}>
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px', background: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' };
const btnPrimary = { backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' };
const btnSecondary = { backgroundColor: '#2f3644', color: 'white', border: '1px solid #3d465d', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' };
const btnDanger = { backgroundColor: '#c0392b', color: 'white', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' };
const thStyle = { padding: 12, color: 'white', fontSize: 13, fontWeight: 'bold', textAlign: 'center' };
const tdStyle = { padding: 12, color: 'white', fontSize: 13, textAlign: 'center' };

export default BoreholeLegends;
